import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { PlinkoService } from '../plinko.service';
import { SessionService } from '@core/services/session.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FairnessButtonComponent } from '../../../core/components/fairness-button/fairness-button.component';
import { GameIconComponent } from '../../../core/components/game-icon/game-icon.component';
import { InputOptionsComponent } from '../../../core/components/input-options/input-options.component';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { ToastrService } from 'ngx-toastr';
import { OldSliderComponent } from '@core/components/old-slider/old-slider.component';

@Component({
  selector: 'app-plinko-bet-section',
  templateUrl: './plinko-bet-section.component.html',
  styleUrls: ['./plinko-bet-section.component.scss'],
  host: {
    class: 'box-black-third rounded-6',
  },
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FairnessButtonComponent,
    GameIconComponent,
    InputOptionsComponent,
    BalanceIconComponent,
    OldSliderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlinkoBetSectionComponent {
  betInputOptions = [
    {
      label: '1/2',
      fn: () => this.setBet('half'),
    },
    {
      label: '2x',
      fn: () => this.setBet('double'),
    },
    {
      label: 'Max',
      fn: () => this.setBet('max'),
    },
  ];

  plinkoInputOptions = [
    {
      label: '-',
      fn: () => this.setRows('minus'),
    },
    {
      label: '+',
      fn: () => this.setRows('plus'),
    },
    {
      label: 'Min',
      fn: () => this.setRows('min'),
    },
    {
      label: 'Max',
      fn: () => this.setRows('max'),
    },
  ];

  betButtons = [
    {
      label: '+1',
      fn: () => this.setBet(1),
    },
    {
      label: '+10',
      fn: () => this.setBet(10),
    },
    {
      label: '+25',
      fn: () => this.setBet(25),
    },
    {
      label: '+50',
      fn: () => this.setBet(50),
    },
  ];

  betAmount = signal<number>(0.1);
  manual = true;

  balls = 1;

  autoActivated = signal<boolean>(false);
  autoBalls = signal<number>(0);
  ballsLeft = signal<number>(0);

  _autoInterval: any;

  constructor(
    public plinkoService: PlinkoService,
    public sessionService: SessionService,
    public clipboardService: ClipboardService,
    private toastrService: ToastrService
  ) {}

  ngOnDestroy() {
    if (this._autoInterval) {
      clearInterval(this._autoInterval);
    }
  }

  private setRows(type: 'minus' | 'plus' | 'min' | 'max') {
    const MIN = 8;
    const MAX = 16;
    this.plinkoService.currentRows.update((prev) => {
      let newValue = prev;

      switch (type) {
        case 'minus':
          newValue = prev - 1;
          break;
        case 'plus':
          newValue = prev + 1;
          break;
        case 'min':
          newValue = MIN;
          break;
        case 'max':
          newValue = MAX;
          break;
      }

      if (newValue < MIN) {
        this.toastrService.error(
          `You can only play with at least ${MIN} rows!`
        );
        return MIN;
      }

      if (newValue > MAX) {
        this.toastrService.error(`You can only play up to ${MAX} rows!`);
        return MAX;
      }

      return newValue;
    });
  }

  setBet(value: number | 'half' | 'double' | 'max') {
    const { MAX_BET, MIN_BET } = this.plinkoService;

    this.betAmount.update((prev) => {
      const newValue =
        value === 'half'
          ? Math.floor(prev / 2)
          : value === 'double'
          ? prev * 2
          : value === 'max'
          ? Math.min(MAX_BET, this.sessionService.currentBalance())
          : prev + (typeof value === 'number' ? value : 0);

      return Math.min(MAX_BET, Math.max(MIN_BET, newValue));
    });
  }

  async drop() {
    await this.plinkoService.placeBet(this.balls, this.betAmount());
  }

  startAuto() {
    this.autoActivated.set(true);
    const isInfinite = Number(this.autoBalls()) === 0;
    this.ballsLeft.set(Number(this.autoBalls()));

    this._autoInterval = setInterval(async () => {
      if (isInfinite) {
        await this.plinkoService.placeBet(1, this.betAmount());
        return;
      }

      const ballsLeft = this.ballsLeft();

      if (ballsLeft > 0) {
        await this.plinkoService.placeBet(1, this.betAmount());
        this.ballsLeft.update((prev) => Number(prev) - 1);
      } else {
        this.autoActivated.set(false);
        clearInterval(this._autoInterval);
      }
    }, 300);
  }

  stopAuto() {
    this.autoActivated.set(false);
    clearInterval(this._autoInterval);
    this.ballsLeft.set(0);
  }

  changeManual(option: boolean) {
    if (this.autoActivated()) {
      this.stopAuto();
    }

    this.manual = option;
  }
}
